import React, { useState, useId } from 'react';
import ReactMarkdown from 'react-markdown';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './Accordion.scss';

export interface AccordionItem {
  /** Header title (the clickable question). */
  title: string;
  /** Expandable body content (markdown supported). */
  content: string;
}

export type AccordionLayout = 'stacked' | 'sticky-rail';
export type AccordionVariant = 'default' | 'card';

/**
 * @polySemanticUsage About this block: a collapsible content block where users click to expand or
 * collapse individual items. Use it for FAQs, detailed feature explanations, policy details, or
 * any content that benefits from progressive disclosure to reduce page overwhelm.
 * Progressive-disclosure list of expandable items and the preferred block for
 * FAQ content: map each question to item{N}Title and its answer to item{N}Content. Accordion hides
 * item content by default (defaultOpenIndex is -1, fully collapsed, and at most one item can start
 * open) and has no expand-all, so do not use it when the source page shows the content already
 * expanded — content that is valuable at first glance belongs in a non-collapsing block such as
 * ContentGrid. Accordion has no numbering feature; when source items are numbered but the order
 * carries no real meaning, omit the numbers rather than embedding them in item{N}Title text.
 * Grouped FAQs: if the source FAQ section is made of several titled groups of questions (for
 * example "Account registration", "Fees", "Shipping"), do not flatten them into one accordion and
 * do not put the group title in item{N}Title. Create one Accordion per group and put the group
 * title in groupTitle; it renders as a smaller H3 row above that group's questions. Put the
 * section's page-level heading and subheading on the first Accordion only (heading, subheading),
 * so the page reads: heading, subheading, group title, questions, then the next group.
 */
export interface AccordionProps {
  /** @polyLabel Item 1 Title */
  item1Title: string;
  /** @polyType RichText
   *  @polyLabel Item 1 Content */
  item1Content: string;

  /** @polyLabel Item 2 Title */
  item2Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 2 Content */
  item2Content?: string;

  /** @polyLabel Item 3 Title */
  item3Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 3 Content */
  item3Content?: string;

  /** @polyLabel Item 4 Title */
  item4Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 4 Content */
  item4Content?: string;

  /** @polyLabel Item 5 Title */
  item5Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 5 Content */
  item5Content?: string;

  /** @polyLabel Item 6 Title */
  item6Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 6 Content */
  item6Content?: string;

  /** @polyLabel Item 7 Title */
  item7Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 7 Content */
  item7Content?: string;

  /** @polyLabel Item 8 Title */
  item8Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 8 Content */
  item8Content?: string;

  /** @polyLabel Item 9 Title */
  item9Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 9 Content */
  item9Content?: string;

  /** @polyLabel Item 10 Title */
  item10Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 10 Content */
  item10Content?: string;

  /** @polyLabel Item 11 Title */
  item11Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 11 Content */
  item11Content?: string;

  /** @polyLabel Item 12 Title */
  item12Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 12 Content */
  item12Content?: string;

  /** @polyLabel Item 13 Title */
  item13Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 13 Content */
  item13Content?: string;

  /** @polyLabel Item 14 Title */
  item14Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 14 Content */
  item14Content?: string;

  /** @polyLabel Item 15 Title */
  item15Title?: string;
  /** @polyType RichText
   *  @polyLabel Item 15 Content */
  item15Content?: string;

  /**
   * When true, opening an item closes the others. Default false (each toggles independently).
   * @polyType Boolean
   */
  singleOpen?: boolean;

  /**
   * Index of the item open on first render. -1 (default) starts fully collapsed.
   * @polyType Number
   */
  defaultOpenIndex?: number;

  /**
   * Optional section heading rendered above the items (stacked) or beside them (sticky-rail).
   * @polyType Text
   */
  heading?: string;

  /**
   * Optional subheading under the heading. Markdown supported for inline links.
   * @polyType RichText
   */
  subheading?: string;

  /**
   * Optional title for this group of questions, rendered as a smaller H3 row directly above
   * the items (below the heading and subheading when those are set). Use it when a FAQ section
   * is split into titled groups, one Accordion per group.
   * @polyType Text
   * @polyLabel Group title
   * @polyHelp Title for this group of questions, shown above the items in a smaller heading style. Leave empty when the accordion is not one group of a larger FAQ.
   */
  groupTitle?: string;

  /**
   * Placement of the heading relative to the items.
   * 'stacked' (default) puts the heading above the accordion at full width.
   * 'sticky-rail' puts the heading in a left column that sticks while the items
   * scroll on the right; it collapses to stacked below the tablet breakpoint.
   * @polyType SelectList
   * @polyOptions Stacked=stacked,Sticky left rail=sticky-rail
   * @polyDefault stacked
   */
  layout?: AccordionLayout;

  /**
   * Visual treatment of the content group.
   * 'default' shows divider lines on a transparent background.
   * 'card' wraps the heading and items together in an off-white, rounded, padded box.
   * @polyType SelectList
   * @polyOptions Default=default,Card=card
   * @polyDefault default
   */
  variant?: AccordionVariant;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

const PlusMinusIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <span className="accordion__icon" data-expanded={expanded} aria-hidden="true">
    <span className="accordion__icon-bar accordion__icon-bar--horizontal" />
    <span className="accordion__icon-bar accordion__icon-bar--vertical" />
  </span>
);

export const Accordion: React.FC<AccordionProps> = ({
  item1Title, item1Content,
  item2Title, item2Content,
  item3Title, item3Content,
  item4Title, item4Content,
  item5Title, item5Content,
  item6Title, item6Content,
  item7Title, item7Content,
  item8Title, item8Content,
  item9Title, item9Content,
  item10Title, item10Content,
  item11Title, item11Content,
  item12Title, item12Content,
  item13Title, item13Content,
  item14Title, item14Content,
  item15Title, item15Content,
  singleOpen = false,
  defaultOpenIndex = -1,
  heading,
  subheading,
  groupTitle,
  layout = 'stacked',
  variant = 'default',
  anchorId,
}) => {
  const items: AccordionItem[] = [
    { title: item1Title, content: item1Content },
    { title: item2Title, content: item2Content },
    { title: item3Title, content: item3Content },
    { title: item4Title, content: item4Content },
    { title: item5Title, content: item5Content },
    { title: item6Title, content: item6Content },
    { title: item7Title, content: item7Content },
    { title: item8Title, content: item8Content },
    { title: item9Title, content: item9Content },
    { title: item10Title, content: item10Content },
    { title: item11Title, content: item11Content },
    { title: item12Title, content: item12Content },
    { title: item13Title, content: item13Content },
    { title: item14Title, content: item14Content },
    { title: item15Title, content: item15Content },
  ].filter((i): i is AccordionItem => !!(i.title && i.content));
  const [openIndices, setOpenIndices] = useState<Set<number>>(
    () => (defaultOpenIndex >= 0 ? new Set([defaultOpenIndex]) : new Set())
  );

  const toggle = (index: number) => {
    setOpenIndices((prev) => {
      const next = new Set(singleOpen ? [] : prev);
      if (prev.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const instanceId = useId();

  if (!items || items.length === 0) {
    return null;
  }

  const hasHeading = Boolean(heading || subheading);
  const headingTitleId = `accordion-heading-${instanceId}`;
  const groupTitleId = `accordion-group-title-${instanceId}`;
  const labelledBy = heading ? headingTitleId : groupTitle ? groupTitleId : undefined;

  const rootClasses = [
    'accordion',
    `accordion--${layout}`,
    `accordion--${variant}`,
    hasHeading ? 'accordion--has-heading' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      {...blockAnchorAttributes(anchorId)}
      className={rootClasses}
      data-poly-block="Accordion"
      aria-labelledby={labelledBy}
    >
      <div className="accordion__inner">
        <div className="accordion__group">
          {hasHeading && (
            <div className="accordion__heading">
              {heading && (
                <h2 id={headingTitleId} className="accordion__heading-title">
                  {heading}
                </h2>
              )}
              {subheading && (
                <div className="accordion__heading-subtitle">
                  <ReactMarkdown>{subheading}</ReactMarkdown>
                </div>
              )}
            </div>
          )}

          <div className="accordion__items">
            {groupTitle && (
              <h3 id={groupTitleId} className="accordion__group-title">
                {groupTitle}
              </h3>
            )}
            {items.map((item, index) => {
              const expanded = openIndices.has(index);
              const headerId = `accordion-header-${instanceId}-${index}`;
              const panelId = `accordion-panel-${instanceId}-${index}`;
              return (
                <div className="accordion__item" key={index}>
                  <button
                    id={headerId}
                    type="button"
                    className="accordion__header"
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={() => toggle(index)}
                  >
                    <span className="accordion__title">{item.title}</span>
                    <PlusMinusIcon expanded={expanded} />
                  </button>
                  <div
                    className="accordion__panel"
                    id={panelId}
                    data-expanded={expanded}
                    role="region"
                    aria-labelledby={headerId}
                  >
                    <div className="accordion__panel-inner">
                      <ReactMarkdown
                        components={{ p: ({ children }) => <p className="accordion__content">{children}</p> }}
                      >
                        {item.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
